import type { CoreAccount } from '../types/financial';
import type { OnboardingAsset } from '../types/onboarding';
import { todayAsOfDate } from '../utils/balanceObservations';
import { apiRequest } from './apiClient';
import type { SyncedPortfolioSnapshot } from './portfolioSyncService';

export type PortfolioImportAccount = {
  /** Stable across snapshot days for Trends; assigned on first save. */
  accountId?: string;
  label: string;
  type: string;
  balance: number;
  currency?: string;
  broker?: string | null;
  expectedReturnPct?: number;
  isInvestment: boolean;
  source?: 'import' | 'manual';
  ownerUserId?: string | null;
  hiddenFromHousehold?: boolean;
};

export type PortfolioImportResponse = {
  success: boolean;
  broker: string | null;
  asOfDate: string | null;
  totalBalance: number;
  accounts: PortfolioImportAccount[];
  /** Persisted Mongo row (cross-device sync). */
  snapshot?: SyncedPortfolioSnapshot | null;
};

/** Extract screenshot accounts and save them to Mongo for the local upload day. */
export async function extractPortfolioFromScreenshot(
  imageData: string,
  asOfDate: string = todayAsOfDate(),
): Promise<PortfolioImportResponse> {
  return apiRequest<PortfolioImportResponse>('/imports/portfolio', {
    method: 'POST',
    body: JSON.stringify({ imageData, asOfDate }),
  });
}

/** Keep exact screenshot label — never rewrite to catalog names. */
export function portfolioAccountsToCore(
  accounts: PortfolioImportAccount[],
): CoreAccount[] {
  const stamp = Date.now();
  return accounts.map((account, index) => ({
    id: account.accountId?.trim() || `import_invest_${stamp}_${index}`,
    kind: 'asset' as const,
    type: 'investment',
    label: account.label.trim(),
    balance: account.balance,
    isInvestment: true,
    expectedReturnPct: account.expectedReturnPct,
  }));
}

export function portfolioAccountsToDraftAssets(
  accounts: PortfolioImportAccount[],
): OnboardingAsset[] {
  return accounts.map((account) => ({
    type: 'investment',
    label: account.label.trim(),
    value: account.balance,
    isInvestment: true,
    expectedReturnPct: account.expectedReturnPct,
  }));
}
